import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import DarkBackground from '../components/DarkBackground';

// Obtener las claves de las variables de entorno
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const isComponentMounted = useRef(true); // Referencia para verificar si el componente está montado
  const navigate = useNavigate();

  // Establecer la referencia de montaje y limpiar al desmontar
  useEffect(() => {
    isComponentMounted.current = true;
    
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isComponentMounted.current) return;
    
    setError(null);
    setLoading(true);

    try {
      console.log("Intentando iniciar sesión con email:", email);
      
      // Comprobar que tenemos la clave API correcta antes de intentar la autenticación
      if (!supabase || !supabase.auth) {
        throw new Error("El cliente de Supabase no está inicializado correctamente");
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Error detallado de autenticación:", error);
        throw error;
      }

      if (data && data.user) {
        console.log("Inicio de sesión exitoso, usuario:", data.user.id);
        
        // Comprobar si la sesión se creó correctamente
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Error al verificar la sesión:", sessionError);
          throw sessionError;
        }
        
        if (!sessionData.session) {
          console.error("No se creó sesión tras inicio exitoso");
          throw new Error('No se pudo crear la sesión, inténtalo de nuevo');
        }
        
        // Redireccionar al usuario a la página home en lugar de la raíz
        navigate('/home');
      } else {
        // Si llegamos aquí, no hay error pero tampoco datos de usuario
        throw new Error('Respuesta de autenticación inválida');
      }
    } catch (err: any) {
      console.error('Error de login:', err);
      
      // Manejar mensajes de error específicos
      let errorMessage = err.message || 'Error al iniciar sesión';
      
      // Formatear mensajes de error comunes para hacerlos más amigables
      if (errorMessage.includes('Invalid login credentials')) {
        errorMessage = 'Credenciales inválidas. Verifica tu email y contraseña.';
      } else if (errorMessage.includes('Invalid API key')) {
        errorMessage = 'Error de configuración del servidor. Por favor, intenta de nuevo o contacta a soporte.';
      } else if (errorMessage.includes('JWT')) {
        errorMessage = 'Error con el token de sesión. Por favor, intenta de nuevo.';
      }
      
      if (isComponentMounted.current) {
        setError(errorMessage);
      }
    } finally {
      if (isComponentMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isComponentMounted.current) return;
    
    setError(null);
    setLoading(true);

    try {
      // Proceder directamente con el registro sin validación de reCAPTCHA
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/home`
        }
      });

      if (error) throw error;

      if (data && data.user) {
        if (data.user.identities && data.user.identities.length === 0) {
          if (isComponentMounted.current) {
            setError('Este correo ya está registrado. Intenta iniciar sesión.');
          }
        } else if (data.user && data.session) {
          navigate('/home');
        } else {
          if (isComponentMounted.current) {
            setError('Revisa tu correo para confirmar tu cuenta.');
          }
        }
      }
    } catch (err: any) {
      console.error('Error de registro:', err);
      if (isComponentMounted.current) {
        setError(err.message || 'Error al registrarse');
      }
    } finally {
      if (isComponentMounted.current) {
        setLoading(false);
      }
    }
  };
  
  const handleGoogleLogin = async () => {
    if (!isComponentMounted.current) return;
    
    setError(null);
    setLoading(true);

    try {
      // Siempre redireccionar a /home después del login con Google, independientemente del entorno
      const redirectUrl = `${window.location.origin}/home`;

      console.log('Redireccionando a:', redirectUrl);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
        }
      });

      if (error) throw error;
      
      // No es necesario navegar aquí ya que el proveedor OAuth redirecciona 
      // automáticamente después de la autenticación
    } catch (err: any) {
      console.error('Error en login con Google:', err);
      if (isComponentMounted.current) {
        setError(err.message || 'Error al iniciar sesión con Google');
        setLoading(false);
      }
    }
  };

  // Cambia entre modos de autenticación y resetea errores
  const toggleAuthMode = () => {
    if (!isComponentMounted.current) return;
    
    setError(null);
    setIsSignUp(!isSignUp);
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <DarkBackground />
      <div className="absolute inset-0 z-10 flex items-center justify-center">
        <div className="max-w-md w-full p-6">
          <div className="bg-gray-900/80 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-800/50 overflow-hidden">
            {/* Header de login/registro */}
            <div className="flex">
              <button
                onClick={() => toggleAuthMode()}
                className={`flex-1 py-4 text-center transition-all duration-300 ${
                  !isSignUp 
                    ? 'bg-blue-900/20 text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white border-b border-gray-800/50'
                }`}
              >
                Iniciar Sesión
              </button>
              <button
                onClick={() => toggleAuthMode()}
                className={`flex-1 py-4 text-center transition-all duration-300 ${
                  isSignUp 
                    ? 'bg-indigo-900/20 text-indigo-400 border-b-2 border-indigo-400'
                    : 'text-gray-400 hover:text-white border-b border-gray-800/50'
                }`}
              >
                Registrarse
              </button>
            </div>

            <div className="p-8">
              <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-white mb-2 flex justify-center items-center">
                  To<span className="text-blue-400">Watch</span>
                  <div className="w-10 h-10 ml-3 bg-blue-900/20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                </h1>
                <p className="text-gray-300">{isSignUp ? 'Crea una cuenta para comenzar' : 'Inicia sesión para continuar'}</p>
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-800/30 text-red-300 px-4 py-3 rounded-lg mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={isSignUp ? handleSignUp : handleLogin} className="space-y-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-900/20 to-indigo-900/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                    <div className="relative">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-gray-800/50 border border-gray-800/50 rounded-xl px-5 py-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 pl-12"
                        placeholder="correo@ejemplo.com"
                        required
                      />
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Contraseña</label>
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-900/20 to-indigo-900/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                    <div className="relative">
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-gray-800/50 border border-gray-800/50 rounded-xl px-5 py-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 pl-12"
                        placeholder={isSignUp ? "Mínimo 6 caracteres" : "Tu contraseña"}
                        required
                      />
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full px-6 py-4 rounded-xl flex items-center justify-center space-x-2 disabled:opacity-50 shadow-lg transition-all duration-300 ${
                      isSignUp
                        ? 'bg-gradient-to-r from-indigo-900/70 to-indigo-700/70 text-white hover:from-indigo-800/70 hover:to-indigo-600/70 shadow-indigo-900/20'
                        : 'bg-gradient-to-r from-blue-900/70 to-blue-700/70 text-white hover:from-blue-800/70 hover:to-blue-600/70 shadow-blue-900/20'
                    }`}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>{isSignUp ? 'Creando cuenta...' : 'Iniciando sesión...'}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {isSignUp ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                          )}
                        </svg>
                        <span>{isSignUp ? 'Registrarse' : 'Iniciar Sesión'}</span>
                      </>
                    )}
                  </button>

                  {/* Botón de login con Google */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-800"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-gray-900 text-gray-400">o continúa con</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="w-full bg-gray-800/50 hover:bg-gray-700/50 border border-gray-800/50 rounded-xl px-6 py-4 flex items-center justify-center space-x-2 text-white shadow-lg transition-all duration-300 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span>{isSignUp ? 'Registrarse con Google' : 'Iniciar sesión con Google'}</span>
                  </button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  {isSignUp ? '¿Ya tienes una cuenta? ' : '¿No tienes una cuenta? '}
                  <button 
                    onClick={() => toggleAuthMode()} 
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {isSignUp ? 'Inicia sesión' : 'Regístrate'}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 